import { mediaQuery } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, tint } = themeVariables;

// Layout breakpoint widths (px) the hero switches at. The design system is deliberately
// layout-agnostic and ships no breakpoint tokens, so the carousel owns the widths it responds to.
const BREAKPOINT_M = 768;
const BREAKPOINT_XL = 1440;

/*
 * The hero is full-bleed and each slide is narrower than the viewport so the neighbouring slides
 * peek in, split evenly either side. These two are a pair: the offset must be half of what the slide
 * leaves over, or the track no longer rests with a slide centred. The progress bar uses the offset to
 * line up with the active slide rather than the viewport.
 */
const SLIDE_WIDTH = '86%';
const SLIDE_PEEK_OFFSET = '7%';

/**
 * Pins an absolutely positioned overlay to the active slide rather than the viewport, which is wider
 * because the neighbouring slides peek in. Kept here with the slide widths it has to agree with: from
 * XL up the slides are full width and there is no peek, so the inset has to disappear with it.
 */
export const slideInset = css`
  left: ${SLIDE_PEEK_OFFSET};
  right: ${SLIDE_PEEK_OFFSET};

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      left: 0;
      right: 0;
    `,
  )};
`;

export const singleItem = css`
  width: 100%;
`;

export const carousel = css`
  position: relative;
  width: 100%;
`;

export const viewport = css`
  overflow: hidden;
  position: relative;
`;

export const container = css`
  display: flex;
  /*
   * Pre-centre the first slide for the server-rendered / pre-hydration frame so it matches where
   * Embla rests it once initialised (align: 'center'). Shifting the track right by the peek offset
   * centres slide 1. Embla replaces this with its own inline transform on init (inline wins), so the
   * image doesn't jump on hydration; only the left-hand looped clone fills in afterwards.
   */
  transform: translate3d(${SLIDE_PEEK_OFFSET}, 0, 0);

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      transform: translate3d(0, 0, 0);
    `,
  )};
`;

export const slide = css`
  flex: 0 0 ${SLIDE_WIDTH};
  min-width: 0;
  margin-right: var(${spacing.xxs});

  ${mediaQuery(
    { min: BREAKPOINT_XL },
    css`
      flex: 0 0 100%;
      min-width: 0;
      margin-right: var(${spacing.xxs});
    `,
  )};
`;

/*
 * Distance from the viewport edge to the arrow, carried over from the previous bespoke control so the
 * arrows stay where they were. One number for design to adjust.
 */
const CONTROL_INSET = '84px';
const CONTROL_SIZE = '44px';

/*
 * The arrows are the carousel's own button rather than the design-system IconButton, which has a
 * single transparent appearance and no variant to change it. A transparent arrow disappears into the
 * hero imagery, so these are a solid surface circle. Hover, focus and sizing follow the design
 * system's own solid round button (FavouriteButton) so the two stay consistent, and every value
 * comes from the theme rather than being hard-coded.
 */
export const control = css`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  width: ${CONTROL_SIZE};
  height: ${CONTROL_SIZE};
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: var(${borderRadius.full.desktop});
  background-color: var(${colors.surface.default});
  color: var(${colors.content.default});
  cursor: pointer;
  /* Separates the circle from a pale image, where a white edge alone would not read. */
  box-shadow: 0 0 4px 0 color-mix(in srgb, var(${colors.content.primary}) 10%, transparent);
  /*
   * Hidden below M. Safe as display: none now that the autoplay clock lives in the progress bar,
   * which shows at every screen size. It could not be before: a display: none subtree suspends CSS
   * animations, and the clock used to be the ring inside this button, so hiding it this way would
   * have stopped autoplay on small screens. display: none also keeps the arrows out of the tab order
   * while they are invisible.
   */
  display: none;

  &:hover {
    background-color: color-mix(
      in srgb,
      var(${colors.content.default}) var(${tint.hover}),
      var(${colors.surface.default}) calc(100% - var(${tint.hover}))
    );
  }

  &:focus-visible {
    outline: 2px solid var(${colors.stroke.focus});
    outline-offset: 2px;
  }

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      display: flex;
    `,
  )};
`;

export const controlPrevious = css`
  left: ${CONTROL_INSET};
`;

export const controlNext = css`
  right: ${CONTROL_INSET};
`;
